import moment from "moment";
import { currentUserHasMoney, MONEY_MASK } from "../../lib/permissions";

// Backend Money fields may arrive as a plain number, a string, or
// { amount, currency } / { value, currency }. Extract the amount safely.
export const moneyAmount = (value) => {
  if (value === null || value === undefined) return null;
  if (typeof value === "number") return Number.isFinite(value) ? value : null;
  if (typeof value === "string") {
    const n = Number(value);
    return Number.isFinite(n) ? n : null;
  }
  if (typeof value === "object") {
    const n = Number(value.amount ?? value.value);
    return Number.isFinite(n) ? n : null;
  }
  return null;
};

export const moneyCurrency = (value, fallback = "NGN") => {
  if (value && typeof value === "object" && value.currency) return value.currency;
  return fallback;
};

// Reuses the bonusesAdmin formatter shape — Intl.NumberFormat in en-NG with the
// campaign's own currency code. Bare ad-hoc calls live in two places already
// (bonusesAdmin/utils.js, adminReports/TableComponent.jsx) and neither accepts
// a currency code, so this is the one path that handles the Money shape.
export const formatMoney = (value, currencyOverride) => {
  // financials.view gate: mask monetary values for users without it.
  if (!currentUserHasMoney()) return MONEY_MASK;
  const amount = moneyAmount(value);
  if (amount === null) return "—";
  const currency = currencyOverride || moneyCurrency(value, "NGN");
  try {
    return new Intl.NumberFormat("en-NG", {
      style: "currency",
      currency,
      minimumFractionDigits: 0,
      maximumFractionDigits: 2,
    }).format(amount);
  } catch (e) {
    return `${currency} ${amount.toLocaleString()}`;
  }
};

const FULL_FEE_WAIVER = "Full fee waiver";

// Human-readable description of what the campaign awards. Mirrors the
// SEMANTIC MAPPING in the campaign spec — kept as a pure function so a future
// rewording changes one place.
//
// Rules of the mapping (do not paraphrase, they are exact):
//   FEE_WAIVER campaign_type           -> "Full fee waiver" (regardless of reward_kind)
//   DISCOUNT + FEE_WAIVER reward kind  -> "Full fee waiver"
//   DISCOUNT + FLAT                    -> "₦{amount} off the fee"
//   DISCOUNT + PERCENT                 -> "{n}% off the fee"
//   CASHBACK + FLAT                    -> "₦{amount} cashback per qualifying tx"
//   CASHBACK + PERCENT                 -> "{n}% cashback of the amount"
//   GIVEAWAY / REFERRAL_BONUS          -> display-only label, no runtime semantics
export const rewardSummary = (campaign = {}) => {
  if (!campaign) return "—";
  const kind = campaign.reward_kind;
  const type = campaign.campaign_type;

  if (type === "FEE_WAIVER") return FULL_FEE_WAIVER;
  if (type === "DISCOUNT" && kind === "FEE_WAIVER") return FULL_FEE_WAIVER;

  if (type === "DISCOUNT" && kind === "FLAT") {
    return `${formatMoney(campaign.flat_amount, campaign.reward_currency)} off the fee`;
  }
  if (type === "DISCOUNT" && kind === "PERCENT") {
    return campaign.percent_value != null
      ? `${campaign.percent_value}% off the fee`
      : "— off the fee";
  }
  if (type === "CASHBACK" && kind === "FLAT") {
    return `${formatMoney(campaign.flat_amount, campaign.reward_currency)} cashback per qualifying tx`;
  }
  if (type === "CASHBACK" && kind === "PERCENT") {
    return campaign.percent_value != null
      ? `${campaign.percent_value}% cashback of the amount`
      : "— cashback of the amount";
  }
  if (type === "GIVEAWAY") return "Giveaway (display only)";
  if (type === "REFERRAL_BONUS") return "Referral bonus (display only)";
  return "—";
};

// Returns a warning string when reward fields are internally inconsistent or
// missing, otherwise null. The spec specifically asks us to surface — rather
// than silently render the wrong copy — a FEE_WAIVER campaign whose reward_kind
// disagrees, and a value-driven combo missing its value.
export const rewardWarning = (campaign = {}) => {
  if (!campaign) return null;
  const type = campaign.campaign_type;
  const kind = campaign.reward_kind;

  if (type === "FEE_WAIVER" && kind && kind !== "FEE_WAIVER") {
    return `Misconfigured: FEE_WAIVER campaign has reward kind ${kind}. Set reward kind to FEE_WAIVER.`;
  }
  if ((type === "DISCOUNT" || type === "CASHBACK") && kind === "FLAT" && !campaign.flat_amount) {
    return "Missing flat amount for this reward.";
  }
  if ((type === "DISCOUNT" || type === "CASHBACK") && kind === "PERCENT" && campaign.percent_value == null) {
    return "Missing percent value for this reward.";
  }
  return null;
};

// Compact redemption display: "12 / 100" with cap, just "12" without.
// Returns "—" when the backend hasn't annotated the row yet (null/undefined),
// not "0" — zero is a meaningful answer ("not redeemed yet") and we don't want
// to conflate it with "we don't know".
export const formatRedemptions = (campaign = {}) => {
  const used = campaign.redeemed_count;
  const cap = campaign.max_total_redemptions;
  if (used === null || used === undefined) return "—";
  return cap ? `${used} / ${cap}` : `${used}`;
};

// Budget usage as {pct, level, hasBudget}. Level keys feed into CSS classes —
// kept here so the colour decision is testable and reusable.
export const budgetState = (campaign = {}) => {
  const cap = moneyAmount(campaign.max_budget);
  if (!cap || cap <= 0) {
    return { pct: 0, level: "none", hasBudget: false };
  }
  const used = moneyAmount(campaign.total_cost) || 0;
  const pct = Math.max(0, Math.min(100, (used / cap) * 100));
  let level = "normal";
  if (pct >= 95) level = "danger";
  else if (pct >= 75) level = "warning";
  return { pct, level, hasBudget: true, used, cap };
};

// Currently-running = ACTIVE + enabled + clock is between start and end. The
// state field alone isn't enough because a campaign can be ACTIVE with start_at
// in the future or end_at in the past.
export const isRunningNow = (campaign = {}, now = Date.now()) => {
  if (!campaign) return false;
  if (campaign.state !== "ACTIVE") return false;
  if (campaign.is_enabled === false) return false;
  if (campaign.start_at && moment(campaign.start_at).valueOf() > now) return false;
  if (campaign.end_at && moment(campaign.end_at).valueOf() < now) return false;
  return true;
};

// Operator → human-readable token. Used by humanizeRule. NE/IN/NOT_IN read more
// naturally with English phrasing than with mathematical symbols.
const OPERATOR_TEXT = {
  EQ: "is",
  NE: "is not",
  GT: ">",
  GTE: "≥",
  LT: "<",
  LTE: "≤",
  IN: "in",
  NOT_IN: "not in",
};

const formatList = (value) => {
  if (Array.isArray(value)) return value.length ? value.join(", ") : "—";
  if (value === null || value === undefined || value === "") return "—";
  return String(value);
};

// Renders a single eligibility rule as a short English sentence. The value
// column read here depends on the rule_type per the spec's rule-type table —
// reading from a different column would either misrepresent the rule or
// silently drop data. Unknown rule_types fall back to "Custom rule" so a future
// backend addition doesn't crash the page.
export const humanizeRule = (rule = {}) => {
  if (!rule || !rule.rule_type) return "Custom rule";
  const op = OPERATOR_TEXT[rule.operator] || (rule.operator ? rule.operator.toLowerCase() : "is");

  switch (rule.rule_type) {
    case "USER_SEGMENT":
      return `User state ${op} ${rule.value_text || "—"}`;
    case "USER_EMAIL":
      return `Email ${op} ${rule.value_text || "—"}`;
    case "USER_EMAILS":
      return `Email ${op} [${formatList(rule.value_json)}]`;
    case "EMPLOYER":
      return `Employer ${op} [${formatList(rule.value_json)}]`;
    case "USER_TAG":
      return `Tag ${op} [${formatList(rule.value_json)}]`;
    case "ACTIVITY_LEVEL":
      return `Settled transactions (last 30 days) ${op} ${rule.value_number ?? "—"}`;
    case "SALARY_THRESHOLD":
      return `Salary ${op} ${formatMoney(rule.value_number)}`;
    case "LAST_TRANSACTION_DATE":
      return `${rule.value_number ?? "—"} days since last settled transaction`;
    case "DATE_RANGE": {
      const v = rule.value_json || {};
      return `User created between ${v.start || "—"} and ${v.end || "—"}`;
    }
    default:
      return "Custom rule";
  }
};

// "Applies to all active users" is meaningfully different from an empty array
// in the UI — make that distinction explicit so admins don't think the page is
// just empty.
export const summarizeEligibility = (rules) => {
  if (!Array.isArray(rules) || rules.length === 0) {
    return { text: "Applies to all active users", empty: true };
  }
  return { text: `${rules.length} eligibility rule${rules.length === 1 ? "" : "s"}`, empty: false };
};

// Summarizes which delivery channels a campaign will use. Returns
// { text, channels, isEmpty, warning? } so the caller decides how to render —
// the same data shape feeds both the list-row chip and the preview card.
// `isEmpty` flips the chip to a warning style and surfaces the tooltip text.
export const channelSummary = (campaign = {}) => {
  const channels = [];
  if (campaign.send_in_app) channels.push("Push");
  if (campaign.send_sms) channels.push("SMS");
  if (campaign.send_email) channels.push("Email");

  if (channels.length === 0) {
    return {
      text: "No notification",
      channels: [],
      isEmpty: true,
      warning: "This campaign will not notify anyone — toggle at least one channel.",
    };
  }
  if (channels.length === 1) {
    return { text: `${channels[0]} only`, channels, isEmpty: false };
  }
  return { text: channels.join(" + "), channels, isEmpty: false };
};

// Tooltip text for the running dot — explains the verdict so admins can see
// *why* a campaign isn't live without going into the detail view.
export const runningTooltip = (campaign = {}, now = Date.now()) => {
  if (!campaign) return "";
  if (campaign.state !== "ACTIVE") return `Not running — state is ${campaign.state || "unknown"}`;
  if (campaign.is_enabled === false) return "Not running — disabled";
  if (campaign.start_at && moment(campaign.start_at).valueOf() > now) {
    return `Starts ${moment(campaign.start_at).fromNow()}`;
  }
  if (campaign.end_at && moment(campaign.end_at).valueOf() < now) {
    return `Ended ${moment(campaign.end_at).fromNow()}`;
  }
  return campaign.end_at ? `Running — ends ${moment(campaign.end_at).fromNow()}` : "Running";
};
